import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { MomentResponse } from "../../shared/interfaces/moment";
import { MomentImageCache } from "./moment-image-cache";
import { CachedMomentImage } from "./cached-moment-image";

/**
 * Run async work for each cache one after another (Images Lambda: never parallel).
 * Promise chaining instead of await-in-loop for eslint.
 */
function runCachesSequentially(
  caches: MomentImageCache[],
  runOne: (cache: MomentImageCache) => Promise<HTMLImageElement[]>,
  onBatch: (collected: HTMLImageElement[]) => void,
): Promise<HTMLImageElement[]> {
  const ordered = _.sortBy(caches, (c) => c.index);
  return ordered.reduce(
    (chain, cache) => chain.then(async (collected) => {
      try {
        const imgs = await runOne(cache);
        const next = [...collected, ...(imgs || []).filter(Boolean)];
        onBatch(next);
        return next;
      } catch {
        return collected;
      }
    }),
    Promise.resolve([] as HTMLImageElement[]),
  );
}

export class MomentImageCacheService {
  private momentImagesRefs: Array<any> = [];

  momentImageCaches: Array<MomentImageCache> = [];

  firstImages$ = new BehaviorSubject<HTMLImageElement[]>(null);

  remainingImages$ = new BehaviorSubject<HTMLImageElement[]>([]);

  gifImages$ = new BehaviorSubject<MomentImageCache[]>([]);
  // gifImages$ = new BehaviorSubject<string[]>([]);

  loading = false;

  generateGIFs = false;

  generateGIFForSharing = false;

  /** Clears preloaded inventory when row/GA seat context changes; keeps the same instance (gifImages$ subscription). */
  resetForSeatContextChange(): void {
    this.momentImageCaches = [];
    this.momentImagesRefs = [];
    this.firstImages$.next(null);
    this.remainingImages$.next([]);
    this.gifImages$.next([]);
    this.loading = false;
  }

  getCacheForMoment(momentId: string): MomentImageCache {
    return _.find(this.momentImageCaches, (c) => c.momentId === momentId);
  }

  cacheForIndex(index: number) {
    return _.find(this.momentImageCaches, (c) => c.index === index);
  }

  async preCacheMomentImages(momentList: Array<MomentResponse>, fromMoment?: number, toMoment?: number): Promise<void> {
    this.loading = true;
    this.loadMomentImageInventory(momentList, fromMoment, toMoment);
    // One first-slide GET at a time (Images Lambda). Defer slides 2+ unless GIF pregen.
    await this.fetchFirstImagesSequentially();
    if (this.generateGIFs) {
      await this.fetchRemainingImagesSequentially();
      await this.renderGifs();
    }
    this.loading = false;
  }

  async renderSingleGIF(momentId: string, forSharing?: boolean): Promise<string> {
    const momentImagesCache = this.momentImageCaches.find((cache) => cache.momentId === momentId);
    if (momentImagesCache) {
      // GIF needs remaining slides; load them for this moment only, one at a time.
      await momentImagesCache.fetchFirstImages(false);
      await momentImagesCache.renderGif(forSharing);
      this.gifImages$.next([momentImagesCache]);
    }
    return momentImagesCache?.gifImage?.src;
  }

  async renderSingleGifForSharing(momentId: string): Promise<string> {
    const momentImagesCache = this.momentImageCaches.find((cache) => cache.momentId === momentId);
    if (!momentImagesCache) {
      return null;
    }

    await momentImagesCache.fetchFirstImages(false);
    const gifImage = await momentImagesCache.renderGifForSharing();

    return gifImage?.src || null;
  }

  async renderAllGIFs(): Promise<void> {
    await this.fetchRemainingImagesSequentially();
    await this.renderGifs();
  }

  /** Load each moment's slide #1 one after another (never in parallel). */
  async fetchFirstImagesSequentially(): Promise<void> {
    await runCachesSequentially(
      this.momentImageCaches,
      (cache) => cache.fetchFirstImages(true),
      (collected) => this.firstImages$.next(collected),
    );
  }

  /** Load slides 2+ one image at a time across moments (GIF / explicit remaining only). */
  async fetchRemainingImagesSequentially(): Promise<void> {
    await runCachesSequentially(
      this.momentImageCaches,
      (cache) => cache.fetchFirstImages(false),
      (collected) => this.remainingImages$.next(collected),
    );
  }

  async fetchFirstImages(fetchFirst: boolean, firstThreeMoments: boolean) {
    const momentCaches = firstThreeMoments
      ? this.firstThreeMomentCaches()
      : this.momentImageCaches;
    const collected = await runCachesSequentially(
      momentCaches,
      (cache) => cache.fetchFirstImages(fetchFirst),
      (batch) => {
        if (fetchFirst) {
          this.firstImages$.next(batch);
        } else {
          this.remainingImages$.next(batch);
        }
      },
    );
    return [collected];
  }

  firstThreeMomentCaches() {
    return _.filter(this.momentImageCaches, (c) => {
      return c.index < 3;
    });
  }

  async renderGifs() {
    const promises: Promise<HTMLImageElement>[] = [];
    for (const cache of this.momentImageCaches) {
      promises.push(cache.renderGif());
    }
    const resultPromise = Promise.all(promises);
    resultPromise
      .then(() => {
        this.gifImages$.next(_.flatten(this.momentImageCaches));
      })
      .catch(() => {
        // GIF build can fail if any slide failed to load; do not leave a dangling rejection.
      });
    return resultPromise;
  }

  loadMomentImageInventory(momentList: Array<MomentResponse>, fromMoment?: number, toMoment?: number) {
    _.each(momentList, (momentResponse, momentListIndex) => {
      const skipPreload = (fromMoment !== undefined && momentListIndex < fromMoment)
        || (toMoment !== undefined && momentListIndex >= toMoment);

      if (!skipPreload) {
        let momentCache = this.getCacheForMoment(momentResponse._id);

        if (!momentCache) {
          momentCache = new MomentImageCache();
          this.momentImageCaches.push(momentCache);
        }

        momentCache.index = momentListIndex;
        momentCache.momentId = momentResponse._id;
        const links = [...momentResponse.momentoLinks];

        _.each(links.reverse(), (imageLink, momentIndex) => {
          let cachedImage = _.find(
            momentCache.momentImages,
            (i) => i.imageUrl === imageLink,
          );
          if (!cachedImage) {
            cachedImage = new CachedMomentImage();
            momentCache.momentImages.push(cachedImage);
          }
          cachedImage.index = momentIndex;
          cachedImage.imageUrl = imageLink;
        });

        if (momentResponse.sponsorOverlay && momentResponse.sponsorOverlayImage) {
          let overlayCachedImage = momentCache.sponsorOverlay;
          if (!overlayCachedImage) {
            overlayCachedImage = new CachedMomentImage();
            momentCache.sponsorOverlay = overlayCachedImage;
          }
          overlayCachedImage.index = -1;
          overlayCachedImage.imageUrl = momentResponse.sponsorOverlayImage;
        }
      }
    });
  }
}
